import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { errorMessage } from "@/lib/api";
import { useTenantConfig } from "@/hooks/useTenantConfig";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Loader2, ShieldCheck } from "lucide-react";

/**
 * Security — final step of tenant setup. Password policy, MFA and session
 * controls, persisted into tenant_configurations.securityPolicy via the
 * real /configuration PATCH (the securityPolicy JSONB the contract already
 * reserves). Enforcement lives in tis-auth; this screen records the
 * tenant's chosen policy.
 */
const DEFAULT_POLICY = { mfaRequired: false, minPasswordLength: 12, sessionTimeoutMinutes: 60, allowedDomains: "" };

export default function AdminSecurity() {
  const { config, loading, save, error: cfgError, setError } = useTenantConfig();
  const [policy, setPolicy] = useState(DEFAULT_POLICY);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (config) setPolicy({ ...DEFAULT_POLICY, ...(config.securityPolicy || {}) });
  }, [config]);

  const set = (patch) => setPolicy((prev) => ({ ...prev, ...patch }));

  const onSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await save({ securityPolicy: policy });
      setSaved(true);
    } catch (err) {
      const m = errorMessage(err);
      if (m) setError(m);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout variant="tenant">
      <h1 className="font-heading text-2xl font-bold">Security</h1>
      <p className="mt-1 text-muted-foreground">Password, authentication and session policies for your tenant.</p>
      {cfgError && <p className="mt-6 text-sm text-destructive" data-testid="security-error">{cfgError}</p>}
      {loading && <Loader2 className="mt-8 h-6 w-6 animate-spin text-primary" />}

      {!loading && (
        <div className="mt-6 max-w-3xl space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base"><ShieldCheck className="h-4 w-4" /> Authentication</CardTitle>
              <CardDescription>Enforced by the central Auth platform for everyone in your tenant.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between rounded-md border border-border px-4 py-3">
                <div>
                  <p className="text-sm font-medium">Require multi-factor authentication</p>
                  <p className="text-xs text-muted-foreground">Every user must set up MFA to sign in.</p>
                </div>
                <Switch checked={!!policy.mfaRequired} onCheckedChange={(v) => set({ mfaRequired: v })} data-testid="security-mfa" aria-label="Require MFA" />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="minpw">Minimum password length</Label>
                  <Input id="minpw" type="number" min={8} max={64} className="mt-1.5" data-testid="security-minpw"
                    value={policy.minPasswordLength} onChange={(e) => set({ minPasswordLength: Number(e.target.value) })} />
                </div>
                <div>
                  <Label>Session timeout</Label>
                  <Select value={String(policy.sessionTimeoutMinutes)} onValueChange={(v) => set({ sessionTimeoutMinutes: Number(v) })}>
                    <SelectTrigger className="mt-1.5" data-testid="security-timeout"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[15, 30, 60, 120, 480].map((m) => <SelectItem key={m} value={String(m)}>{m} minutes</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label htmlFor="domains">Allowed email domains (optional)</Label>
                <Input id="domains" className="mt-1.5" data-testid="security-domains" placeholder="acme.com, acme.io"
                  value={policy.allowedDomains} onChange={(e) => set({ allowedDomains: e.target.value })} />
                <p className="mt-1 text-xs text-muted-foreground">Comma-separated. Restricts which email domains can be invited.</p>
              </div>
            </CardContent>
          </Card>
          <div className="flex items-center gap-3">
            <Button onClick={onSave} disabled={saving} data-testid="security-save-btn">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save changes
            </Button>
            {saved && <p className="text-sm text-emerald-600" data-testid="security-saved">Saved.</p>}
          </div>
        </div>
      )}
    </Layout>
  );
}
